import { useState, useMemo, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "@/context/GlobalContext";
import { useBrand, findBoardTag } from "@/lib/brand-context";
import { BoardTag, Keyword, Article, BOARD_CONFIG } from "@/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Search, Upload, ChevronDown, MoreHorizontal,
  Sparkles, X, TrendingUp, Database, Library, FileText, ArrowRight,
  Eye, Share2, Copy, CheckCheck, Edit3, Trash2, Send,
} from "lucide-react";
import ImportModal from "./ImportModal";
import { ArticleMarkdown, cleanArticleContent, copyArticleToClipboard } from "@/lib/articleContent";

/* ─── Types ───────────────────────────────────────────────────── */
type TabKey = "seed" | "knowledge" | "articles" | "social";

/* ─── Source badge config ─────────────────────────────────────── */
const SOURCE_STYLE: Record<string, string> = {
  seo:     "bg-blue-50 text-blue-600",
  sem:     "bg-violet-50 text-violet-600",
  opinion: "bg-amber-50 text-amber-600",
};
const SOURCE_LABEL: Record<string, string> = {
  seo: "SEO", sem: "SEM", opinion: "Opinion",
};

/* ─── Volume formatter ────────────────────────────────────────── */
function fmtVol(n?: number) {
  if (!n) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/* ─── Mock GSC performance rows for articles ─────────────────── */
interface ArticleRow {
  id: string;
  title: string;
  keyword: string;
  intentBoard: string;
  status: "published" | "draft";
  createdAt: string;
  impressions: string;
  clicks: string;
  ctr: string;
  /** Editable content stored locally for mock rows (not persisted to backend) */
  content?: string;
}
const MOCK_ARTICLES: ArticleRow[] = [
  { id: "ma1", title: "10 Cara Pinjaman Online Aman & Terpercaya 2026", keyword: "cara pinjaman online aman", intentBoard: "edukasi", status: "published", createdAt: "2026-04-01", impressions: "18.4k", clicks: "1,230", ctr: "6.7%" },
  { id: "ma2", title: "Bunga Pinjol Terendah: Perbandingan 8 Platform OJK", keyword: "bunga pinjol terendah", intentBoard: "kompetitor", status: "published", createdAt: "2026-03-28", impressions: "12.1k", clicks: "876",   ctr: "7.2%" },
  { id: "ma3", title: "Easycash vs KreditPintar: Mana Lebih Cepat Cair?",  keyword: "easycash vs kreditpintar", intentBoard: "kompetitor", status: "published", createdAt: "2026-03-22", impressions: "9.6k",  clicks: "544",   ctr: "5.7%" },
  { id: "ma4", title: "Cara Lapor Pinjol Ilegal ke OJK & Satgas PASTI",    keyword: "lapor pinjol ilegal ojk",  intentBoard: "edukasi",    status: "published", createdAt: "2026-03-18", impressions: "7.3k",  clicks: "398",   ctr: "5.5%" },
  { id: "ma5", title: "Syarat & Limit Pinjaman Easycash hingga Rp 20 Juta",keyword: "limit pinjaman easycash",  intentBoard: "konversi",   status: "draft",     createdAt: "2026-04-05", impressions: "—",     clicks: "—",     ctr: "—"   },
  { id: "ma6", title: "Apa Itu BI Checking & Cara Membersihkannya",         keyword: "bi checking cara bersihkan", intentBoard: "edukasi", status: "draft",     createdAt: "2026-04-07", impressions: "—",     clicks: "—",     ctr: "—"   },
];

/* ─── Social Snippets mock data ───────────────────────────────── */
type SnippetType = "Script" | "Copy";

interface SocialSnippetRow {
  id: string;
  keyword: string;
  /** "Script" = video hook/script (TikTok/Reels), "Copy" = banner/ad text (FB/IG/LinkedIn) */
  type: SnippetType;
  preview: string;
  createdAt: string;
  fullContent: string;
}

const LS_SOCIAL_SNIPPETS = "seo_social_snippets";

/** Default rows shown until / unless localStorage has saved snippets */
const INITIAL_SOCIAL_SNIPPETS: SocialSnippetRow[] = [
  {
    id: "ss1",
    keyword: "cara pinjaman online",
    type: "Script",
    preview: "🎣 Hook: Mau pinjam uang tapi takut kena pinjol ilegal? Ini 3 ciri platform yang sudah terdaftar OJK...",
    fullContent:
      "🎣 Hook (0–3s): Mau pinjam uang tapi takut kena pinjol ilegal?\n\n" +
      "🎯 Key Points (3–50s):\n" +
      "✅ Cek logo OJK di halaman utama aplikasi\n" +
      "✅ Bunga maksimal 0,3% per hari sesuai regulasi\n" +
      "✅ Tidak meminta akses kontak HP tanpa izin\n\n" +
      "💥 CTA (50–60s): Follow akun ini untuk tips keuangan aman. Cek deskripsi untuk link panduan lengkap!",
    createdAt: "2026-04-05",
  },
  {
    id: "ss2",
    keyword: "easycash pinjaman cepat",
    type: "Copy",
    preview: "Butuh dana darurat? Easycash hadir sebagai solusi pinjaman online terdaftar OJK. ✅ Proses cepat ✅ Transparan ✅ Aman...",
    fullContent:
      "Butuh dana darurat? Easycash hadir sebagai solusi pinjaman online terdaftar OJK.\n\n" +
      "✅ Proses pengajuan cepat, hanya 5 menit\n" +
      "✅ Biaya transparan, tanpa biaya tersembunyi\n" +
      "✅ Terdaftar dan diawasi OJK — aman & legal\n\n" +
      "Pelajari syarat & ketentuannya sekarang 👇\n\n" +
      "#PinjamanOnline #Easycash #OJK #KeuanganAman #FinansialSehat",
    createdAt: "2026-04-07",
  },
  {
    id: "ss3",
    keyword: "bunga pinjol terendah",
    type: "Copy",
    preview: "Bandingkan 5 platform pinjol dengan bunga terendah yang sudah terdaftar OJK 2026. Pilih yang paling sesuai...",
    fullContent:
      "📊 5 Platform Pinjol Bunga Terendah 2026 (OJK Terdaftar)\n\n" +
      "Sebelum mengajukan pinjaman, pastikan kamu membandingkan bunga terlebih dahulu.\n\n" +
      "💡 Tips memilih:\n" +
      "→ Perhatikan APR (Annual Percentage Rate), bukan hanya bunga harian\n" +
      "→ Cek biaya admin dan penalti keterlambatan\n" +
      "→ Baca ulasan pengguna di Google Play & App Store\n\n" +
      "Simpan postingan ini untuk referensimu! 🔖\n\n" +
      "#PinjamanOnline #Pinjol #BungaRendah #OJK2026",
    createdAt: "2026-04-08",
  },
];

/* ─── Type badge config ────────────────────────────────────────── */
const TYPE_STYLE: Record<SnippetType, { cls: string; label: string }> = {
  Script: { cls: "bg-purple-100 text-purple-700", label: "Script" },
  Copy:   { cls: "bg-blue-100   text-blue-700",   label: "Copy"   },
};

/* ─── Main component ──────────────────────────────────────────── */
export default function ContentHubWorkspace() {
  const navigate = useNavigate();
  const { keywords, setKeywords, articles, setArticles } = useGlobalContext();
  const { config } = useBrand();

  const boardTags: BoardTag[] =
    config.boardTags?.length
      ? config.boardTags
      : Object.entries(BOARD_CONFIG).map(([k, v]) => ({ key: k, ...v }));

  /* ── UI state ─────────────────────────────────────────────────── */
  const [activeTab, setActiveTab]             = useState<TabKey>("seed");
  const [activeKBCategory, setActiveKBCategory] = useState(boardTags[0]?.key ?? "edukasi");
  const [search, setSearch]                   = useState("");
  const [selectedIds, setSelectedIds]         = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen]           = useState(false);
  const [dropOpen, setDropOpen]               = useState(false);
  const [importSrc, setImportSrc]             = useState<"seo" | "sem" | "opinion">("seo");
  const [openMenuId, setOpenMenuId]           = useState<string | null>(null);
  const [menuPosition, setMenuPosition]       = useState<{ top: number; left: number } | null>(null);
  const [previewArticle, setPreviewArticle]   = useState<ArticleRow | null>(null);
  const [editingKeyword, setEditingKeyword]   = useState<Keyword | null>(null);
  const [copiedSnippetId, setCopiedSnippetId] = useState<string | null>(null);
  const [copiedArticleId, setCopiedArticleId] = useState<string | null>(null);
  const [actionToast, setActionToast]         = useState<string | null>(null);
  /* Social Snippets inline-edit state */
  const [editingSnippetId, setEditingSnippetId] = useState<string | null>(null);
  const [editValue, setEditValue]               = useState("");
  const [socialSnippets, setSocialSnippets]       = useState<SocialSnippetRow[]>(() =>
    INITIAL_SOCIAL_SNIPPETS.map(s => ({ ...s }))
  );
  const [socialSnippetsHydrated, setSocialSnippetsHydrated] = useState(false);
  const [previewSnippet, setPreviewSnippet]       = useState<SocialSnippetRow | null>(null);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(LS_SOCIAL_SNIPPETS);
      if (!raw) {
        setSocialSnippetsHydrated(true);
        return;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        setSocialSnippets(parsed as SocialSnippetRow[]);
      }
    } catch {
      /* ignore */
    }
    setSocialSnippetsHydrated(true);
  }, []);

  useEffect(() => {
    if (!socialSnippetsHydrated) return;
    try {
      localStorage.setItem(LS_SOCIAL_SNIPPETS, JSON.stringify(socialSnippets));
    } catch {
      /* ignore */
    }
  }, [socialSnippets, socialSnippetsHydrated]);

  /* ── Derived data ─────────────────────────────────────────────── */
  const filteredKeywords: Keyword[] = useMemo(() =>
    keywords.filter(kw =>
      !search ||
      kw.keyword.toLowerCase().includes(search.toLowerCase()) ||
      kw.raw.toLowerCase().includes(search.toLowerCase())
    ), [keywords, search]);

  const kbKeywords: Keyword[] = useMemo(() =>
    filteredKeywords.filter(kw => kw.intentBoard === activeKBCategory),
    [filteredKeywords, activeKBCategory]
  );

  const filteredArticles: Article[] = useMemo(() =>
    articles.filter(a =>
      !search || a.title.toLowerCase().includes(search.toLowerCase())
    ), [articles, search]);

  /* ── Shared rows lifted out so `{tab.count}` 与表格渲染使用同一数组 ── */
  const articleRows: ArticleRow[] = useMemo(() => {
    const q = search.toLowerCase();
    const realRows: ArticleRow[] = filteredArticles.map(a => ({
      id:          a.id,
      title:       a.title,
      keyword:     a.keyword,
      intentBoard: a.intentBoard,
      status:      a.status as "published" | "draft",
      createdAt:   a.createdAt,
      impressions: "—",
      clicks:      "—",
      ctr:         "—",
    }));
    if (realRows.length > 0) return realRows;
    return MOCK_ARTICLES.filter(m => !q || m.title.toLowerCase().includes(q));
  }, [filteredArticles, search]);

  const filteredSnippets: SocialSnippetRow[] = useMemo(() => {
    const q = search.toLowerCase();
    return socialSnippets.filter(
      s => !q ||
        s.keyword.toLowerCase().includes(q) ||
        s.preview.toLowerCase().includes(q)
    );
  }, [socialSnippets, search]);

  /* ── Selection helpers ────────────────────────────────────────── */
  const allIds = activeTab === "articles"
    ? filteredArticles.map(a => a.id)
    : filteredKeywords.map(k => k.id);

  function toggleOne(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }
  function toggleAll() {
    setSelectedIds(prev =>
      prev.size === allIds.length ? new Set() : new Set(allIds)
    );
  }
  function clearSelection() { setSelectedIds(new Set()); }

  function handleSendToAgent() {
    const selectedKws = keywords.filter(k => selectedIds.has(k.id));
    if (!selectedKws.length) return;
    const kwParam = selectedKws.map(k => encodeURIComponent(k.keyword)).join(",");
    navigate(`/ai-creator?keywords=${kwParam}&board=${selectedKws[0].intentBoard}`);
  }

  function showActionToast(message: string) {
    setActionToast(message);
    setTimeout(() => setActionToast(null), 2500);
  }

  const toast = {
    info(message: string) {
      showActionToast(message);
    },
  };

  function handleDeleteArticle(id: string) {
    const isRealArticle = articles.some(article => article.id === id);

    if (isRealArticle) {
      setArticles(prev => {
        const next = prev.filter(article => article.id !== id);
        try { localStorage.setItem("seo_articles", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    } else {
      const idx = MOCK_ARTICLES.findIndex(article => article.id === id);
      if (idx !== -1) MOCK_ARTICLES.splice(idx, 1);
    }

    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    showActionToast("Article deleted.");
  }

  async function handleCopyArticle(row: ArticleRow) {
    const realArticle = articles.find(article => article.id === row.id);
    const contentToCopy = realArticle?.content ?? row.content ?? `${row.title}\n${row.keyword}`;

    const ok = await copyArticleToClipboard(contentToCopy);
    if (ok) {
      setCopiedArticleId(row.id);
      setTimeout(() => setCopiedArticleId(prev => (prev === row.id ? null : prev)), 1200);
      showActionToast("Article copied with formatting — paste anywhere.");
    } else {
      showActionToast("Failed to copy article content.");
    }
  }

  function handlePushArticle(row: ArticleRow) {
    toast.info("Initiating push to Haiyu System... API connection pending.");

    // TODO: Integration with Haiyu API via /api/push-to-cms
    const isRealArticle = articles.some(article => article.id === row.id);
    if (isRealArticle) {
      setArticles(prev => {
        const next = prev.map(article =>
          article.id === row.id ? { ...article, status: "published" as const } : article
        );
        try { localStorage.setItem("seo_articles", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
      return;
    }

    const idx = MOCK_ARTICLES.findIndex(article => article.id === row.id);
    if (idx !== -1) MOCK_ARTICLES[idx] = { ...MOCK_ARTICLES[idx], status: "published" };
  }

  async function handleCopySnippetRow(s: SocialSnippetRow) {
    const ok = await copyArticleToClipboard(s.fullContent);
    if (ok) {
      setCopiedSnippetId(s.id);
      setTimeout(() => setCopiedSnippetId(prev => (prev === s.id ? null : prev)), 1200);
      showActionToast("Snippet copied with formatting.");
    } else {
      showActionToast("Failed to copy snippet.");
    }
  }

  function handlePushSnippetRow(_s: SocialSnippetRow) {
    toast.info("Push to social channels — API connection pending.");
  }

  function handleDeleteSnippet(id: string) {
    setSocialSnippets(prev => prev.filter(s => s.id !== id));
    if (editingSnippetId === id) {
      setEditingSnippetId(null);
      setEditValue("");
    }
    setPreviewSnippet(prev => (prev?.id === id ? null : prev));
    showActionToast("Snippet deleted.");
  }

  function handleDeleteKnowledgeKeyword(id: string) {
    setKeywords(prev => prev.filter(k => k.id !== id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    showActionToast("Knowledge entry removed.");
  }

  /* ─────────────────────────────────────────────────────────────── */
  return (
    <div className="relative flex flex-col h-full bg-gray-50">

      {/* ── Page header ───────────────────────────────────────────── */}
      <div className="px-8 pt-8 pb-6 bg-white border-b border-gray-100">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Content Hub</h1>
            <p className="text-sm text-gray-400 mt-1">Manage your SEO keywords and content assets.</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              Export CSV
            </button>
            {/* Import dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropOpen(!dropOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                <Upload size={13} />
                Import New Seed
                <ChevronDown size={13} />
              </button>
              {dropOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setDropOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-1 z-20">
                    {(["seo", "sem", "opinion"] as const).map(src => (
                      <button
                        key={src}
                        onClick={() => { setImportSrc(src); setImportOpen(true); setDropOpen(false); }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="text-sm font-medium text-gray-800">Import to {SOURCE_LABEL[src]}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {src === "seo" ? "Organic search keywords"
                           : src === "sem" ? "Paid search keywords"
                           : "Trend & social keywords"}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Pill-style main tabs ──
         * count 绑定的是"当前过滤后真实渲染到表格里的行数"，
         * 这样 search/KB category 等 filter 变化时，tab 数字会实时联动，
         * 不会出现"标签 A / 数据 B / 统计 C"的错位。
         */}
        <div className="flex items-center gap-1 bg-gray-50/80 p-1 rounded-xl">
          {[
            { key: "seed"      as TabKey, label: "Seed Keywords",     icon: Database, count: filteredKeywords.length },
            { key: "knowledge" as TabKey, label: "Knowledge Base",    icon: Library,  count: kbKeywords.length },
            { key: "articles"  as TabKey, label: "Generated Articles",icon: FileText, count: articleRows.length },
            { key: "social"    as TabKey, label: "Social Snippets",   icon: Share2,   count: filteredSnippets.length },
          ].map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              onClick={() => {
                if (key === activeTab) return;
                setActiveTab(key);
                clearSelection();
                setSearch("");
              }}
              className={`flex-1 flex items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md transition-all ${
                activeTab === key
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50"
              }`}
            >
              <Icon size={14} />
              {label}
              <span className={`text-xs px-2 py-0.5 rounded-full tabular-nums ${
                activeTab === key ? "bg-indigo-50 text-indigo-500" : "bg-gray-200 text-gray-500"
              }`}>
                {count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Search bar ────────────────────────────────────────────── */}
      <div className="px-8 py-3 bg-white border-b border-gray-100">
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search keywords..."
            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
          />
        </div>
      </div>

      {/* ── Content area ──────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto pb-28">

        {/* ══ Seed Keywords ══ */}
        {activeTab === "seed" && (
          <div className="px-8 py-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden w-full">
              {/* Sub-header — sits outside the scroll container so it never scrolls away */}
              <div className="px-6 py-3.5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700">Raw Seed Keywords</p>
                <button className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors">
                  Process to Knowledge Base
                </button>
              </div>

              {/* Scroll wrapper — directly and exclusively wraps <table> */}
              <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
              <table className="min-w-[1080px] w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredKeywords.length && filteredKeywords.length > 0}
                        onChange={toggleAll}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200"
                      />
                    </th>
                    {["KEYWORD", "SOURCE", "VOLUME", "IMPRESSIONS", "CLICKS", "SITE CTR", "DATE ADDED", ""].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredKeywords.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-16 text-gray-400 text-sm">
                        No seed keywords yet — use <strong>Import New Seed</strong> to add some.
                      </td>
                    </tr>
                  ) : filteredKeywords.map(kw => (
                    <tr
                      key={kw.id}
                      onClick={() => toggleOne(kw.id)}
                      className={`border-b border-gray-50 last:border-0 cursor-pointer transition-colors ${
                        selectedIds.has(kw.id) ? "bg-indigo-50/50" : "hover:bg-gray-50/60"
                      }`}
                    >
                      <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(kw.id)}
                          onChange={() => toggleOne(kw.id)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200"
                        />
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-gray-800 leading-snug">{kw.keyword}</p>
                        {kw.raw !== kw.keyword && (
                          <p className="text-xs text-gray-400 mt-0.5">{kw.raw}</p>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SOURCE_STYLE[kw.source]}`}>
                          {SOURCE_LABEL[kw.source]}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <TrendingUp size={11} className="text-gray-400" />
                          {fmtVol(kw.volume)}
                        </div>
                      </td>
                      {/* GSC placeholder columns — will be replaced by real data once GSC API is connected */}
                      <td className="px-4 py-3.5 text-xs text-gray-400">—</td>
                      <td className="px-4 py-3.5 text-xs text-gray-400">—</td>
                      <td className="px-4 py-3.5 text-xs text-gray-400">—</td>
                      <td className="px-4 py-3.5 text-xs text-gray-400">{kw.createdAt}</td>
                      <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <div className="relative inline-block">
                          <button
                            onClick={(e) => {
                              if (openMenuId === kw.id) {
                                setOpenMenuId(null);
                                setMenuPosition(null);
                                return;
                              }
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPosition({
                                top: rect.bottom + 4,
                                left: rect.right,
                              });
                              setOpenMenuId(kw.id);
                            }}
                            className="p-1.5 text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
                          >
                            <MoreHorizontal size={15} />
                          </button>
                          {openMenuId === kw.id && (
                            <>
                              {/* click-outside backdrop */}
                              {menuPosition && createPortal(
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setMenuPosition(null);
                                    }}
                                  />
                                  <div
                                    className="fixed w-36 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20"
                                    style={{
                                      top: `${menuPosition.top}px`,
                                      left: `${menuPosition.left}px`,
                                      transform: "translateX(-100%)",
                                    }}
                                  >
                                    <button
                                      onClick={() => {
                                        setEditingKeyword(kw);
                                        setOpenMenuId(null);
                                        setMenuPosition(null);
                                      }}
                                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (window.confirm("Are you sure you want to delete this keyword?")) {
                                          setKeywords(prev => prev.filter(k => k.id !== kw.id));
                                          setOpenMenuId(null);
                                          setMenuPosition(null);
                                        }
                                      }}
                                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </>,
                                document.body
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>{/* end scroll wrapper */}
            </div>
          </div>
        )}

        {/* ══ Knowledge Base ══ */}
        {activeTab === "knowledge" && (
          <div className="px-8 py-5">
            {/* Category sub-tabs */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {boardTags.map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setActiveKBCategory(cat.key)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-all border ${
                    activeKBCategory === cat.key
                      ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                      : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                  }`}
                >
                  {cat.label}
                  <span className="ml-1.5 text-[11px] opacity-60">
                    ({filteredKeywords.filter(k => k.intentBoard === cat.key).length})
                  </span>
                </button>
              ))}
            </div>

            {/* Table — mirrors Seed Keywords structure */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden w-full">
              <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
              <table className="min-w-[1080px] w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === kbKeywords.length && kbKeywords.length > 0}
                        onChange={() =>
                          setSelectedIds(prev =>
                            prev.size === kbKeywords.length
                              ? new Set()
                              : new Set(kbKeywords.map(k => k.id))
                          )
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200"
                      />
                    </th>
                    {["KEYWORD", "STRATEGY", "SOURCE", "VOLUME", "ACTIONS"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kbKeywords.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-16 text-gray-400 text-sm">
                        <Library size={28} className="mx-auto mb-3 text-gray-300" />
                        No keywords in this category yet — import seed keywords and assign them to <strong>{activeKBCategory}</strong>.
                      </td>
                    </tr>
                  ) : kbKeywords.map(kw => {
                    return (
                      <tr
                        key={kw.id}
                        onClick={() => toggleOne(kw.id)}
                        className={`border-b border-gray-50 last:border-0 cursor-pointer transition-colors ${
                          selectedIds.has(kw.id) ? "bg-indigo-50/50" : "hover:bg-gray-50/60"
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(kw.id)}
                            onChange={() => toggleOne(kw.id)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200"
                          />
                        </td>

                        {/* Keyword */}
                        <td className="px-4 py-3.5">
                          <p className="font-medium text-gray-800 leading-snug">{kw.keyword}</p>
                          {kw.raw !== kw.keyword && (
                            <p className="text-xs text-gray-400 mt-0.5">{kw.raw}</p>
                          )}
                        </td>

                        {/* Strategy — inline editable select styled as a badge */}
                        <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                          <select
                            value={kw.intentBoard}
                            onChange={e =>
                              setKeywords(prev =>
                                prev.map(k =>
                                  k.id === kw.id ? { ...k, intentBoard: e.target.value } : k
                                )
                              )
                            }
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 cursor-pointer outline-none appearance-none border-0"
                          >
                            {boardTags.map(t => (
                              <option key={t.key} value={t.key}>{t.label}</option>
                            ))}
                            <option value="unclassified">Unclassified</option>
                          </select>
                        </td>

                        {/* Source badge */}
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${SOURCE_STYLE[kw.source]}`}>
                            {SOURCE_LABEL[kw.source]}
                          </span>
                        </td>

                        {/* Volume */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <TrendingUp size={11} className="text-gray-400" />
                            {fmtVol(kw.volume)}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/ai-creator?keywords=${encodeURIComponent(kw.keyword)}&board=${kw.intentBoard}`
                                )
                              }
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap"
                            >
                              Create Article
                              <ArrowRight size={12} />
                            </button>
                            <TooltipProvider delay={100}>
                              <Tooltip>
                                <TooltipTrigger
                                  title="Remove from Knowledge Base"
                                  type="button"
                                  className="p-1.5 text-gray-400 hover:text-red-500 cursor-pointer transition-colors rounded-lg"
                                  onClick={() => handleDeleteKnowledgeKeyword(kw.id)}
                                >
                                  <Trash2 size={14} />
                                </TooltipTrigger>
                                <TooltipContent>Delete keyword</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>{/* end scroll wrapper */}
            </div>
          </div>
        )}

        {/* ══ Social Snippets ══ */}
        {activeTab === "social" && (() => {
          const rows = filteredSnippets;

          /* ── Inline-edit helpers ────────────────────────────── */
          function startEdit(s: SocialSnippetRow) {
            setEditingSnippetId(s.id);
            setEditValue(s.fullContent);
          }

          function commitEdit(id: string) {
            setSocialSnippets(prev =>
              prev.map(s =>
                s.id === id
                  ? { ...s, fullContent: editValue, preview: editValue.slice(0, 90) + (editValue.length > 90 ? "…" : "") }
                  : s
              )
            );
            setEditingSnippetId(null);
            setEditValue("");
          }

          function cancelEdit() {
            setEditingSnippetId(null);
            setEditValue("");
          }

          return (
            <div className="px-8 py-5">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden w-full">
                <div className="px-6 py-3.5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">Social Media Snippets</p>
                  <p className="text-xs text-gray-400">Generated from AI Creator · Step 3 · Click any preview to edit inline</p>
                </div>
                <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
                  <table className="min-w-[900px] w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
                        <th className="w-10 px-4 py-3">
                          <input type="checkbox" className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" disabled />
                        </th>
                        {["KEYWORD", "TYPE", "CONTENT PREVIEW", "DATE", "ACTIONS"].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 tracking-wider">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-16 text-gray-400 text-sm">
                            No social snippets yet — generate them in AI Creator · Step 3.
                          </td>
                        </tr>
                      ) : rows.map(s => {
                        const isEditing = editingSnippetId === s.id;
                        const badge = TYPE_STYLE[s.type];
                        return (
                          <tr
                            key={s.id}
                            className="border-b border-gray-50 last:border-0 hover:bg-gray-50/40 transition-colors"
                          >
                            {/* Checkbox */}
                            <td className="px-4 py-3.5">
                              <input type="checkbox" className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
                            </td>

                            {/* Keyword */}
                            <td className="px-4 py-3.5 max-w-[140px]">
                              <p className="font-medium text-gray-800 leading-snug truncate">{s.keyword}</p>
                            </td>

                            {/* Type badge — Script (purple) or Copy (blue) */}
                            <td className="px-4 py-3.5">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}>
                                {badge.label}
                              </span>
                            </td>

                            {/* Content Preview — click-to-edit */}
                            <td
                              className="px-4 py-3.5 max-w-sm"
                              onClick={() => { if (!isEditing) startEdit(s); }}
                            >
                              {isEditing ? (
                                /* ── Edit mode ── */
                                <textarea
                                  autoFocus
                                  rows={4}
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  onBlur={() => commitEdit(s.id)}
                                  onKeyDown={e => {
                                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                      e.preventDefault();
                                      commitEdit(s.id);
                                    }
                                    if (e.key === "Escape") {
                                      e.preventDefault();
                                      cancelEdit();
                                    }
                                  }}
                                  className="w-full bg-transparent outline-none ring-2 ring-blue-400 rounded-lg p-2 resize-none text-xs text-gray-700 leading-relaxed focus:ring-blue-500 transition-all"
                                />
                              ) : (
                                /* ── Read mode — invite click ── */
                                <div className="group cursor-text relative">
                                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 group-hover:text-gray-700 transition-colors">
                                    {s.preview}
                                  </p>
                                  {/* Subtle "click to edit" hint on hover */}
                                  <span className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-semibold text-blue-400 bg-blue-50 px-1.5 py-0.5 rounded pointer-events-none">
                                    click to edit
                                  </span>
                                </div>
                              )}
                            </td>

                            {/* Date */}
                            <td className="px-4 py-3.5 text-xs text-gray-400 whitespace-nowrap">{s.createdAt}</td>

                            {/* Actions — same icon row as Generated Articles */}
                            <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                              <TooltipProvider delay={100}>
                                <div className="flex items-center gap-3">
                                  <Tooltip>
                                    <TooltipTrigger
                                      title="Preview snippet"
                                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                      onClick={() => setPreviewSnippet(s)}
                                    >
                                      <Eye size={14} />
                                    </TooltipTrigger>
                                    <TooltipContent>View snippet</TooltipContent>
                                  </Tooltip>

                                  <Tooltip>
                                    <TooltipTrigger
                                      title="Copy snippet"
                                      className={`p-1.5 rounded-lg transition-colors ${
                                        copiedSnippetId === s.id
                                          ? "text-green-600 bg-green-50"
                                          : "text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                                      }`}
                                      onClick={() => { void handleCopySnippetRow(s); }}
                                    >
                                      {copiedSnippetId === s.id ? <CheckCheck size={14} /> : <Copy size={14} />}
                                    </TooltipTrigger>
                                    <TooltipContent>Copy content</TooltipContent>
                                  </Tooltip>

                                  <Tooltip>
                                    <TooltipTrigger
                                      title="Push snippet"
                                      className="p-1.5 text-gray-400 hover:text-blue-600 cursor-pointer transition-colors rounded-lg"
                                      onClick={() => handlePushSnippetRow(s)}
                                    >
                                      <Send size={14} />
                                    </TooltipTrigger>
                                    <TooltipContent>Push to channels</TooltipContent>
                                  </Tooltip>

                                  <Tooltip>
                                    <TooltipTrigger
                                      title="Delete snippet"
                                      className="p-1.5 text-gray-400 hover:text-red-500 cursor-pointer transition-colors rounded-lg"
                                      onClick={() => handleDeleteSnippet(s.id)}
                                    >
                                      <Trash2 size={14} />
                                    </TooltipTrigger>
                                    <TooltipContent>Delete snippet</TooltipContent>
                                  </Tooltip>
                                </div>
                              </TooltipProvider>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══ Generated Articles ══ */}
        {activeTab === "articles" && (() => {
          const rows = articleRows;

          return (
            <div className="px-8 py-5">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden w-full">
                <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
                <table className="min-w-[1080px] w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === rows.length && rows.length > 0}
                          onChange={() =>
                            setSelectedIds(prev =>
                              prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id))
                            )
                          }
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200"
                        />
                      </th>
                      {["ARTICLE", "STATUS", "DATE", "IMPRESSIONS", "CLICKS", "SITE CTR", "ACTIONS"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-16 text-gray-400 text-sm">
                          No articles yet — generate one in AI Creator.
                        </td>
                      </tr>
                    ) : rows.map(a => (
                      <tr
                        key={a.id}
                        onClick={() => toggleOne(a.id)}
                        className={`border-b border-gray-50 last:border-0 cursor-pointer transition-colors ${
                          selectedIds.has(a.id) ? "bg-indigo-50/50" : "hover:bg-gray-50/60"
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(a.id)}
                            onChange={() => toggleOne(a.id)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200"
                          />
                        </td>

                        {/* Article title + keyword */}
                        <td className="px-4 py-3.5 max-w-xs">
                          <p className="font-medium text-gray-800 leading-snug line-clamp-2">{a.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{a.keyword}</p>
                        </td>

                        {/* Status badge */}
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            a.status === "published"
                              ? "bg-green-50 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }`}>
                            {a.status === "published" ? "Published" : "Draft"}
                          </span>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3.5 text-xs text-gray-400 whitespace-nowrap">{a.createdAt}</td>

                        {/* Impressions */}
                        <td className="px-4 py-3.5 text-xs text-gray-500">{a.impressions}</td>

                        {/* Clicks */}
                        <td className="px-4 py-3.5 text-xs text-gray-500">{a.clicks}</td>

                        {/* Site CTR */}
                        <td className="px-4 py-3.5">
                          {a.ctr === "—" ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <span className="text-xs font-medium text-indigo-600">{a.ctr}</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                          <TooltipProvider delay={100}>
                            <div className="flex items-center gap-3">
                              <Tooltip>
                                <TooltipTrigger
                                  title="Preview article"
                                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                  onClick={() => setPreviewArticle(a)}
                                >
                                  <Eye size={14} />
                                </TooltipTrigger>
                                <TooltipContent>View article</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger
                                  title="Copy article"
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    copiedArticleId === a.id
                                      ? "text-green-600 bg-green-50"
                                      : "text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                                  }`}
                                  onClick={() => { void handleCopyArticle(a); }}
                                >
                                  {copiedArticleId === a.id ? <CheckCheck size={14} /> : <Copy size={14} />}
                                </TooltipTrigger>
                                <TooltipContent>Copy content</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger
                                  title="Push article"
                                  className="p-1.5 text-gray-400 hover:text-blue-600 cursor-pointer transition-colors rounded-lg"
                                  onClick={() => handlePushArticle(a)}
                                >
                                  <Send size={14} />
                                </TooltipTrigger>
                                <TooltipContent>Push to Haiyu system</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger
                                  title="Delete article"
                                  className="p-1.5 text-gray-400 hover:text-red-500 cursor-pointer transition-colors rounded-lg"
                                  onClick={() => handleDeleteArticle(a.id)}
                                >
                                  <Trash2 size={14} />
                                </TooltipTrigger>
                                <TooltipContent>Delete article</TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>{/* end scroll wrapper */}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Floating Action Bar ────────────────────────────────────── */}
      <div
        className={`absolute bottom-6 left-6 right-6 transition-all duration-300 ease-out ${
          selectedIds.size > 0
            ? "translate-y-0 opacity-100 pointer-events-auto"
            : "translate-y-4 opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-gray-900 rounded-2xl px-6 py-4 flex items-center gap-4 shadow-2xl">
          <div className="flex-shrink-0">
            <p className="text-[10px] text-gray-500 tracking-widest uppercase">Selected</p>
            <p className="text-lg font-bold text-white leading-tight">
              {selectedIds.size}
              <span className="text-sm font-normal text-gray-400 ml-1">
                {activeTab === "articles" ? "Articles" : "Keywords"}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap flex-1">
            {Array.from(selectedIds).slice(0, 3).map(id => {
              // For keyword tabs: show board tag chip. For article tab: show truncated title.
              const kw = keywords.find(k => k.id === id);
              if (kw) {
                const tag = findBoardTag(boardTags, kw.intentBoard);
                return (
                  <span key={id} className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-700 text-gray-300 uppercase tracking-wider">
                    {tag.label.slice(0, 3)}
                  </span>
                );
              }
              // Article row selected — show keyword label instead
              const art = articles.find(a => a.id === id);
              if (art) {
                return (
                  <span key={id} className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-700 text-gray-300 truncate max-w-[80px]">
                    {art.keyword.slice(0, 12)}
                  </span>
                );
              }
              return null;
            })}
            {selectedIds.size > 3 && (
              <span className="text-[10px] text-gray-500">+{selectedIds.size - 3} more</span>
            )}
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={clearSelection}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-gray-200 text-sm font-medium rounded-xl transition-colors"
            >
              <X size={13} />
              Clear
            </button>
            <button
              onClick={handleSendToAgent}
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-xl transition-colors shadow-lg shadow-indigo-900/40"
            >
              <Sparkles size={13} />
              Send to Agent
            </button>
          </div>
        </div>
      </div>

      {/* ── Article Preview Modal ──────────────────────────────────── */}
      {previewArticle && (
        <ArticlePreviewModal
          row={previewArticle}
          realArticle={articles.find(a => a.id === previewArticle.id) ?? null}
          onClose={() => setPreviewArticle(null)}
          onSaveReal={(id, newContent) =>
            setArticles(prev =>
              prev.map(a => a.id === id ? { ...a, content: newContent } : a)
            )
          }
        />
      )}

      {previewSnippet && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setPreviewSnippet(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col overflow-hidden max-h-[85vh]"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="min-w-0 pr-3">
                <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase mb-0.5">
                  {previewSnippet.type}
                </p>
                <h2 className="text-base font-bold text-gray-900 leading-snug">{previewSnippet.keyword}</h2>
              </div>
              <button
                type="button"
                onClick={() => setPreviewSnippet(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed font-sans">
                {previewSnippet.fullContent}
              </pre>
            </div>
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-gray-400">Created: {previewSnippet.createdAt}</span>
              <button
                type="button"
                onClick={() => setPreviewSnippet(null)}
                className="text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Keyword Modal ─────────────────────────────────────── */}
      {editingKeyword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-base font-bold text-gray-900 mb-5">Edit Seed Keyword</h2>

            <div className="flex flex-col gap-4">
              {/* Keyword */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                  Keyword
                </label>
                <input
                  type="text"
                  value={editingKeyword.keyword}
                  onChange={e => setEditingKeyword({ ...editingKeyword, keyword: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                />
              </div>

              {/* Source */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                  Source
                </label>
                <select
                  value={editingKeyword.source}
                  onChange={e =>
                    setEditingKeyword({
                      ...editingKeyword,
                      source: e.target.value as Keyword["source"],
                    })
                  }
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 bg-white transition-all"
                >
                  <option value="seo">SEO</option>
                  <option value="sem">SEM</option>
                  <option value="opinion">Opinion</option>
                </select>
              </div>

              {/* Volume */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                  Search Volume
                </label>
                <input
                  type="number"
                  value={editingKeyword.volume ?? ""}
                  onChange={e =>
                    setEditingKeyword({
                      ...editingKeyword,
                      volume: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder="e.g. 12500"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingKeyword(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setKeywords(prev =>
                    prev.map(k => (k.id === editingKeyword.id ? editingKeyword : k))
                  );
                  setEditingKeyword(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors"
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import Modal ───────────────────────────────────────────── */}
      {actionToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-5 py-2.5 rounded-xl shadow-xl z-[60] pointer-events-none">
          {actionToast}
        </div>
      )}

      {/* ── Import Modal ───────────────────────────────────────────── */}
      {importOpen && (
        <ImportModal
          source={importSrc}
          keywords={keywords}
          setKeywords={setKeywords}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Article Preview Modal — has its own state so lives as a component
───────────────────────────────────────────────────────────────── */
interface ArticlePreviewModalProps {
  row: ArticleRow;
  /** Full Article from GlobalContext (has HTML content). Null for mock rows. */
  realArticle: import("@/types").Article | null;
  onClose: () => void;
  onSaveReal: (id: string, newContent: string) => void;
}

function ArticlePreviewModal({
  row,
  realArticle,
  onClose,
  onSaveReal,
}: ArticlePreviewModalProps) {
  // Prefer real article content from GlobalContext; fall back to mock row's locally-saved content.
  // We clean any legacy <thinking>…</thinking> wrapping so it never leaks into the preview.
  const rawContent     = realArticle?.content ?? row.content ?? "";
  const displayContent = cleanArticleContent(rawContent);
  const hasContent     = displayContent.trim().length > 0;

  const [isEditing, setIsEditing]       = useState(false);
  const [editedContent, setEditedContent] = useState(displayContent);
  const [toast, setToast]               = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  function handleSave() {
    if (realArticle) {
      onSaveReal(realArticle.id, editedContent);
    } else {
      // Patch the mock array so the edit persists within the session
      const idx = MOCK_ARTICLES.findIndex(m => m.id === row.id);
      if (idx !== -1) MOCK_ARTICLES[idx] = { ...MOCK_ARTICLES[idx], content: editedContent };
    }
    setIsEditing(false);
    showToast("Article updated successfully!");
  }

  function handleCancel() {
    setEditedContent(displayContent);
    setIsEditing(false);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header (protected — do not change layout) ── */}
        <div className="flex items-start justify-between px-7 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex-1 min-w-0 mr-4">
            <p className="text-[10px] font-bold text-indigo-400 tracking-widest uppercase mb-0.5">
              {row.intentBoard} · {row.status === "published" ? "Published" : "Draft"}
            </p>
            <h2 className="text-base font-bold text-gray-900 leading-snug">
              {row.title}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{row.keyword}</p>
          </div>

          {/* Header actions — swap based on editing mode */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-indigo-200"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={() => { setEditedContent(displayContent); setIsEditing(true); }}
                className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 text-gray-600 hover:text-indigo-700 text-sm font-semibold rounded-xl transition-colors"
              >
                <Edit3 size={13} />
                Edit Article
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Body — view vs edit ── */}
        <div className="flex-1 overflow-y-auto px-7 py-6 min-h-0">
          {isEditing ? (
            <textarea
              value={editedContent}
              onChange={e => setEditedContent(e.target.value)}
              className="w-full h-[50vh] p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-300 focus:outline-none resize-none text-sm text-gray-800 leading-relaxed font-mono"
              placeholder="Article content (Markdown or HTML)…"
            />
          ) : hasContent ? (
            <article className="prose prose-sm max-w-none text-gray-800 leading-relaxed prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-strong:text-gray-900 prose-p:leading-relaxed prose-li:my-0.5">
              <ArticleMarkdown content={displayContent} />
            </article>
          ) : (
            <div className="text-center py-16">
              <FileText size={36} className="mx-auto mb-3 text-gray-200" />
              <p className="text-sm font-medium text-gray-500">Full article preview not available</p>
              <p className="text-xs text-gray-400 mt-1.5 max-w-xs mx-auto">
                This article was generated in a previous session.
                Click <strong>Edit Article</strong> above to write content directly.
              </p>
            </div>
          )}
        </div>

        {/* ── Footer (protected — do not change) ── */}
        <div className="px-7 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50/50">
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span>Created: {row.createdAt}</span>
            {row.impressions !== "—" && <span>Impressions: {row.impressions}</span>}
            {row.clicks     !== "—" && <span>Clicks: {row.clicks}</span>}
          </div>
          <button
            onClick={onClose}
            className="text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-sm px-6 py-3 rounded-2xl shadow-xl z-[60] pointer-events-none">
          ✓ {toast}
        </div>
      )}
    </div>
  );
}
